import { useMemo } from 'react'
import type { MultiValue, SingleValue } from 'react-select'

type TValue<IsMulti extends boolean = false, T = string> = IsMulti extends false ? T : T[]
export const useSelectHelpers = <T = string, IsMulti extends boolean = false>(
  options: Option<T>[],
  value?: TValue<IsMulti, T>,
  onChange?:
    | ((value?: TValue<IsMulti, T>) => void)
    | React.Dispatch<React.SetStateAction<Optional<TValue<IsMulti, T>>>>,
) => {
  const handleChange = (option: SingleValue<Option<T>> | MultiValue<Option<T>>) => {
    if (onChange) {
      if (!Array.isArray(option)) {
        onChange((option as Option<T>)?.value as TValue<IsMulti, T>)
      } else {
        const val = (option as Option<T>[]).map((el) => el.value)

        onChange(val as unknown as TValue<IsMulti, T>)
      }
    }
  }
  // Only optionValue is memoised. handleChange used to be inside this memo
  // with deps [options, value], so a consumer whose `onChange` closed over
  // other state kept the arrow from the render that last changed one of
  // those two — and called it with stale values. In the MCP editors that
  // meant a schedule change writing back a config captured several renders
  // earlier, deleting sync setup the admin had just entered. Those editors
  // still keep a `latest*` ref as belt-and-braces, but the staleness was
  // never theirs to fix.
  //
  // Returning a fresh handleChange costs nothing: it is a closure, not the
  // list scan below, and every consumer destructures the result rather than
  // depending on the object's identity.
  //
  // Deliberately NOT useCallback([onChange]) + a memoised return object.
  // That is correct only while handleChange captures nothing but onChange,
  // and it silently becomes wrong the moment someone adds a captured value
  // and does not extend the dep array — which is precisely the bug being
  // fixed here, reintroduced in a form that no longer announces itself.
  // 37 call sites destructure this hook; none memoises on its identity.
  const optionValue = useMemo(() => {
    if (Array.isArray(value)) {
      return options.filter((el, index) => {
        const isOption = value.includes(el.value)
        const isAnotherElem = options.findIndex((option) => option.value === el.value) < index
        return isOption && !isAnotherElem
      })
    }
    return options.find((option) => option.value === value)
  }, [options, value])

  return { optionValue, handleChange }
}

export default useSelectHelpers
